@if (formValues$ | async; as data) {
<div class="flex flex-col">
  <div class="px-6 pt-4 pb-3">
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span
          class="rounded px-3 py-0.5 text-[11px] font-medium uppercase tracking-wide text-white"
          [ngClass]="{
            'bg-[#20527f]': data.lot_type === 'Offer',
            'bg-[#2e7d43]': data.lot_type === 'Bid'
          }"
        >
          {{ data.lot_type }}
        </span>
        @if (data.indication) {
        <span
          class="rounded bg-amber-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
        >
          Indication
        </span>
        }
        <span
          class="font-mono text-[11px] uppercase tracking-widest text-gray-500 dark:text-gray-200"
        >
          Please review before submitting
        </span>
      </div>
      <button
        type="button"
        aria-label="Close"
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800"
        (click)="dialogRef.close({ confirm: false })"
      >
        ✕
      </button>
    </div>
  </div>

  <div
    class="border-y border-amber-200/60 bg-[#fbf6e7] px-6 py-3 dark:border-amber-700/50 dark:bg-[#5a4a22]"
  >
    <div class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <span class="text-lg font-medium dark:text-gray-50">
        {{ data.commodity
        }}@if (data.product_type) { · {{ data.product_type }}}@if (data.grade) {
        · {{ data.grade }}}
      </span>
      <span
        class="whitespace-nowrap font-mono text-xl font-semibold text-amber-600 dark:text-amber-400"
      >
        {{ data.price | priceType }} {{ data.currency }}/{{ data.price_units }}
      </span>
    </div>
    <div class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
      {{ data.volume_value | number }} {{ data.units || "MT" }}
      @if (data.delivery_type) { · {{ data.delivery_type }} } @if
      (data.destination) { · {{ data.destination }} }
    </div>
  </div>

  <mat-dialog-content class="mat-typography">
    <div class="grid grid-cols-1 gap-x-10 tablet-min:grid-cols-2">
      <section class="mb-4">
        <h3 class="section-title">Contract</h3>
        <div class="summary-row">
          <span class="row-label">Market Type</span>
          <span class="row-value">{{ data.market_type }}</span>
        </div>
        <div class="summary-row">
          <span class="row-label">Time Frame</span>
          <span class="row-value">{{ data.time_frame }}</span>
        </div>
      </section>

      <section class="mb-4">
        <h3 class="section-title">Product</h3>
        <div class="summary-row">
          <span class="row-label">Origin</span>
          <span class="row-value">{{ data.origin }}</span>
        </div>
        <div class="summary-row">
          <span class="row-label">Crop</span>
          <span class="row-value">{{ data.commodity }}</span>
        </div>
        @if (data.product_type) {
        <div class="summary-row">
          <span class="row-label">Product Type</span>
          <span class="row-value">{{ data.product_type }}</span>
        </div>
        }
        <div class="summary-row">
          <span class="row-label">Grade</span>
          <span class="row-value">{{ data.grade || "-" }}</span>
        </div>
      </section>

      @if (data.visibleSpecs.length) {
      <section class="mb-4">
        <h3 class="section-title">Specifications</h3>
        @for (spec of data.visibleSpecs; track spec) {
        <div class="summary-row">
          <span class="row-label">{{ spec.title }}</span>
          <span class="row-value">{{ spec.value }}</span>
        </div>
        }
      </section>
      }

      <section class="mb-4">
        <h3 class="section-title">Delivery</h3>
        <div class="summary-row">
          <span class="row-label">Delivery Type</span>
          <span class="row-value">{{ data.delivery_type }}</span>
        </div>
        @if (data.delivery_option) {
        <div class="summary-row">
          <span class="row-label">Delivery Option</span>
          <span class="row-value">{{ data.delivery_option }}</span>
        </div>
        } @if (data.container_type) {
        <div class="summary-row">
          <span class="row-label">Container Type</span>
          <span class="row-value">{{ data.container_type }}</span>
        </div>
        }
        <div class="summary-row">
          <span class="row-label">Location</span>
          <span class="row-value">{{ data.destination }}</span>
        </div>
        @if (data.deliveryWindowControl?.start) {
        <div class="summary-row">
          <span class="row-label">Shipping Window</span>
          <span class="row-value">
            {{ data.deliveryWindowControl.start | date : "MMM d, y" }} -
            {{ data.deliveryWindowControl.end | date : "MMM d, y" }}
          </span>
        </div>
        }
      </section>

      <section class="mb-4">
        <h3 class="section-title">Price &amp; Quantity</h3>
        <div class="summary-row">
          <span class="row-label">Price</span>
          <span class="row-value">
            {{ data.price | priceType }} {{ data.currency }} /
            {{ data.price_units }}
          </span>
        </div>
        <div class="summary-row">
          <span class="row-label">Quantity</span>
          <span class="row-value">
            {{ data.volume_value | number }} {{ data.units || "MT" }}
          </span>
        </div>
      </section>

      <section class="mb-4">
        <h3 class="section-title">Payment Terms</h3>
        <div class="summary-row">
          <span class="row-label">Governing Rule</span>
          <span class="row-value">{{ data.rules_to_govern || "N/A" }}</span>
        </div>
        <div class="summary-row">
          <span class="row-label">Weight / Grade To Govern</span>
          <span class="row-value">{{ data.weight_grade_to_govern }}</span>
        </div>
        <div class="summary-row">
          <span class="row-label">Payment Terms</span>
          <span class="row-value">{{ data.payment_term }}</span>
        </div>
      </section>

      @if (data.documentations?.length) {
      <section class="mb-4 tablet-min:col-span-2">
        <h3 class="section-title">Documentation</h3>
        @for (doc of data.documentations; track doc) { @if (doc.value !== null)
        {
        <div class="summary-row">
          <span class="row-label">{{ doc.title }}</span>
          <span class="row-value">{{ doc.value }}</span>
        </div>
        } }
      </section>
      } @if (data.additional_info) {
      <section class="mb-4 tablet-min:col-span-2">
        <h3 class="section-title">Additional Notes</h3>
        <p class="py-1.5 text-sm dark:text-gray-100">{{ data.additional_info }}</p>
      </section>
      }
    </div>
  </mat-dialog-content>

  <mat-dialog-actions align="end" class="gap-2">
    <button
      mat-stroked-button
      type="button"
      class="back-to-edit-btn"
      (click)="dialogRef.close({ confirm: false })"
    >
      Back to edit
    </button>
    <button
      mat-flat-button
      type="button"
      class="confirm-submit-btn"
      (click)="dialogRef.close({ confirm: true })"
    >
      ✓&nbsp; Confirm &amp; Submit
    </button>
  </mat-dialog-actions>
</div>
}
